import { useCallback, useEffect, useMemo, useRef } from "react";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  CURRENCY_OPTIONS,
  isCryptoWalletCurrency,
} from "@/modules/dashboard/confirmations/constants/currencies.config";
import { getBankOptionsForCurrency } from "@/modules/dashboard/confirmations/constants/paymentMethodsByCurrency.config";
import type { NewConfirmationSelectOption } from "@/modules/dashboard/confirmations/types/new-confirmation.types";
import { PaymentMethodFieldsSection } from "@/modules/dashboard/payments/components/PaymentMethodFieldsSection";
import { validatePaymentMethodFields } from "@/modules/dashboard/payments/utils/validatePaymentMethodFields";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaComboboxField } from "@/shared/components/forms/ColocaComboboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import type {
  ClientAccount,
  CreateClientAccountRequest,
  PublicAccountPaymentMethod,
} from "../../types/clients-api.types";
import { PublicAccountPaymentMethod as PublicAccountPaymentMethodEnum } from "../../types/clients-api.types";
import { buildCreateClientAccountPayload } from "../../utils/buildCreateClientAccountPayload";

/**
 * Bank/method options for the account form — same corridor restrictions as the accounts modal it was
 * extracted from (Binance Pay pins Binance, COP hides Bancolombia / member-only Bre-B, USD hides Zelle).
 */
function getAccountFormBankOptions(
  currencyCode: string,
  paymentMethod: PublicAccountPaymentMethod | "",
  isInternalUser: boolean,
): NewConfirmationSelectOption[] {
  const trimmed = currencyCode.trim();
  if (paymentMethod === PublicAccountPaymentMethodEnum.BINANCE_PAY && trimmed === "USDT") {
    return [{ label: "Binance", value: "BINANCE" }];
  }
  const base = getBankOptionsForCurrency(currencyCode);
  if (trimmed === "COP") {
    if (!isInternalUser) {
      return base.filter((o) => o.value === "Bre-B");
    }
    return base.filter((o) => o.value !== "Bancolombia");
  }
  if (trimmed === "USD") {
    return base.filter((o) => o.value !== "Zelle");
  }
  return base;
}

/** Payment method a currency implies when preselected (mirror of the currency-change handler). */
function paymentMethodForCurrency(currency: string): PublicAccountPaymentMethod | "" {
  const trimmed = currency.trim();
  if (!trimmed) return "";
  return isCryptoWalletCurrency(trimmed)
    ? PublicAccountPaymentMethodEnum.CRYPTO_WALLET
    : PublicAccountPaymentMethodEnum.BANK;
}

/**
 * Static fields via Zod; the dynamic per-method inputs reuse `validatePaymentMethodFields` (PIX rules,
 * Binance UID/email, required fields — shared with the payments form) through `superRefine`, surfaced as
 * `method_fields.<id>` issues so RHF renders them under each input.
 */
const buildAccountSchema = (messages: {
  currency: string;
  paymentMethod: string;
  bank: string;
  aliasMax: string;
  methodFieldRequired: string;
}) =>
  z
    .object({
      payment_method: z.string().min(1, messages.paymentMethod),
      currency: z.string().trim().min(1, messages.currency),
      bank: z.string().trim().min(1, messages.bank),
      alias: z.string().max(255, messages.aliasMax),
      method_fields: z.record(z.string(), z.string()),
    })
    .superRefine((data, ctx) => {
      const methodErrors = validatePaymentMethodFields(data.currency, data.bank, data.method_fields);
      for (const [fieldId, message] of Object.entries(methodErrors)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["method_fields", fieldId],
          message: message ?? messages.methodFieldRequired,
        });
      }
    });

type AccountFormValues = z.infer<ReturnType<typeof buildAccountSchema>>;

export interface ClientAccountCreateFormProps {
  /** Executes the create call — the host chooses the endpoint (client vs organization account). */
  createAccount: (body: CreateClientAccountRequest) => Promise<ClientAccount>;
  /** Called with the created account after the success toast + form reset. */
  onCreated?: (account: ClientAccount) => void;
  /** Preselects this currency (and the payment method it implies) on mount. Unknown codes are ignored. */
  defaultCurrency?: string;
  /**
   * Locks the CURRENCY to `defaultCurrency` (e.g. the dispersion destination currency). The payment method
   * stays selectable among the methods compatible with it (USDT: crypto wallet or Binance Pay).
   */
  lockCurrency?: boolean;
  /**
   * Preselects the method of `defaultCurrency` — a `bank` option of that currency. Together with
   * `defaultMethodFields` it is how the dispersions "save this recipient" action hands over the destination
   * that was just paid, instead of asking for a key the app already holds.
   */
  defaultBank?: string;
  /** Seeds the per-method inputs by field id (`FIELDS_BY_CURRENCY_METHOD`). Requires `defaultBank`. */
  defaultMethodFields?: Record<string, string>;
  /** Disables every field (e.g. while the host has no target client yet). */
  disabled?: boolean;
  /** Lets the host block closing while a create is in flight. */
  onSubmittingChange?: (submitting: boolean) => void;
}

/**
 * "New account" form shared by /app/clients and the dispersions register-account shortcut. RHF + Zod
 * (`useZodForm`) own the field state and validation; the corridor restrictions, cascade rules and payload
 * build are unchanged from the accounts modal it was extracted from. The create call is delegated to the host
 * (client vs organization endpoint) and the currency can be preselected/locked for the dispersion
 * destination. The selects are comboboxes on purpose: the native option popup is OS-painted and cannot be
 * themed reliably — `ColocaComboboxField` renders a fully styled portal instead.
 */
export function ClientAccountCreateForm({
  createAccount,
  onCreated,
  defaultCurrency,
  lockCurrency = false,
  defaultBank,
  defaultMethodFields,
  disabled = false,
  onSubmittingChange,
}: ClientAccountCreateFormProps) {
  const { t } = useTranslations("clients");
  const { t: tConfirmations } = useTranslations("confirmations");
  const { t: tPayments } = useTranslations("payments");
  const { t: tAccountsWallets } = useTranslations("accountsWallets");
  const { t: tGlobal } = useTranslations("global");
  const { isInternalUser } = useAuth();

  const initialCurrency =
    defaultCurrency && CURRENCY_OPTIONS.some((o) => o.value === defaultCurrency.trim())
      ? defaultCurrency.trim()
      : "";
  const currencyLocked = lockCurrency && !!initialCurrency;

  const schema = useMemo(
    () =>
      buildAccountSchema({
        currency: t("accounts_validation_currency"),
        paymentMethod: t("accounts_validation_payment_method"),
        bank: t("accounts_validation_bank"),
        aliasMax: t("accounts_validation_alias_max"),
        methodFieldRequired: tPayments("validation_method_field_required"),
      }),
    [t, tPayments],
  );

  // A seeded method only makes sense against the currency that defines it — `getAccountFormBankOptions` is
  // keyed by the pair, and a bank from another currency would render as a value with no matching option.
  const initialBank = useMemo(() => {
    if (!defaultBank || !initialCurrency) return "";
    const options = getAccountFormBankOptions(
      initialCurrency,
      paymentMethodForCurrency(initialCurrency),
      isInternalUser,
    );
    return options.some((option) => option.value === defaultBank) ? defaultBank : "";
  }, [defaultBank, initialCurrency, isInternalUser]);

  const defaults = useMemo<AccountFormValues>(
    () => ({
      payment_method: paymentMethodForCurrency(initialCurrency),
      currency: initialCurrency,
      bank: initialBank,
      alias: "",
      method_fields: initialBank ? (defaultMethodFields ?? {}) : {},
    }),
    [initialCurrency, initialBank, defaultMethodFields],
  );

  const form = useZodForm(schema, defaults);

  const currency = form.watch("currency");
  const paymentMethod = form.watch("payment_method") as PublicAccountPaymentMethod | "";
  const bank = form.watch("bank");
  const methodFields = form.watch("method_fields");

  // A currency/bank change invalidates the method-specific inputs (their field set changes with the pair).
  // Guarded against its own first run: effects fire after the initial render too, and clearing there used to
  // be a harmless no-op only because the defaults were empty. With a seeded destination it would wipe the
  // prefill a frame after painting it.
  const lastMethodPair = useRef(`${defaults.currency}|${defaults.bank}`);
  useEffect(() => {
    const pair = `${currency}|${bank}`;
    if (pair === lastMethodPair.current) return;
    lastMethodPair.current = pair;
    form.setValue("method_fields", {});
    form.clearErrors("method_fields");
  }, [currency, bank, form]);

  // Safety net for combos filtered out of the options (COP+Bancolombia, USD+Zelle).
  useEffect(() => {
    if ((currency === "COP" && bank === "Bancolombia") || (currency === "USD" && bank === "Zelle")) {
      form.setValue("bank", "");
    }
  }, [currency, bank, form]);

  // The currency implies the payment method (crypto vs bank); Binance Pay survives a USDT re-pick.
  // Runs AFTER the field wrote the new currency (ColocaComboboxField's onValueChange contract).
  const handleCurrencyChange = useCallback(
    (value: string | null) => {
      const next = value ?? "";
      const trimmed = next.trim();
      let nextPaymentMethod: PublicAccountPaymentMethod | "" = !trimmed
        ? ""
        : isCryptoWalletCurrency(trimmed)
          ? PublicAccountPaymentMethodEnum.CRYPTO_WALLET
          : PublicAccountPaymentMethodEnum.BANK;
      if (
        form.getValues("payment_method") === PublicAccountPaymentMethodEnum.BINANCE_PAY &&
        trimmed === "USDT"
      ) {
        nextPaymentMethod = PublicAccountPaymentMethodEnum.BINANCE_PAY;
      }
      form.setValue("payment_method", nextPaymentMethod);
      const options = getAccountFormBankOptions(next, nextPaymentMethod, isInternalUser);
      if (!options.some((o) => o.value === form.getValues("bank"))) {
        form.setValue("bank", "");
      }
    },
    [form, isInternalUser],
  );

  const handlePaymentMethodChange = useCallback(
    (value: string | null) => {
      const method = (value ?? "") as PublicAccountPaymentMethod | "";
      form.setValue("bank", "");
      form.setValue("method_fields", {});
      form.clearErrors();
      // The lock pins the CURRENCY, never the method: the options are already filtered to compatible
      // methods, so nothing else must move — and clearing the method falls back to the currency's
      // default instead of unlocking the currency.
      if (currencyLocked) {
        if (!method) {
          form.setValue("payment_method", paymentMethodForCurrency(initialCurrency));
        }
        return;
      }
      if (!method) {
        form.setValue("currency", "");
        return;
      }
      if (method === PublicAccountPaymentMethodEnum.BINANCE_PAY) {
        form.setValue("currency", "USDT");
        return;
      }
      if (method === PublicAccountPaymentMethodEnum.CRYPTO_WALLET) {
        if (!isCryptoWalletCurrency(form.getValues("currency"))) {
          form.setValue("currency", "USDT");
        }
      } else {
        form.setValue(
          "currency",
          CURRENCY_OPTIONS.find((o) => !isCryptoWalletCurrency(o.value))?.value ?? "COP",
        );
      }
    },
    [form, currencyLocked, initialCurrency],
  );

  const handleMethodFieldChange = useCallback(
    (fieldId: string, value: string) => {
      form.setValue("method_fields", { ...form.getValues("method_fields"), [fieldId]: value });
      form.clearErrors(`method_fields.${fieldId}`);
    },
    [form],
  );

  // RHF nests the superRefine issues under `method_fields.<id>` — flatten to the section's errors record.
  const methodFieldsErrors = form.formState.errors.method_fields;
  const methodErrors = useMemo(() => {
    const flat: Partial<Record<string, string>> = {};
    if (methodFieldsErrors) {
      for (const [fieldId, err] of Object.entries(methodFieldsErrors)) {
        const message = (err as { message?: unknown } | undefined)?.message;
        if (typeof message === "string") flat[fieldId] = message;
      }
    }
    return flat;
  }, [methodFieldsErrors]);

  const handleSubmit = useCallback(
    async (values: AccountFormValues) => {
      onSubmittingChange?.(true);
      try {
        const body = buildCreateClientAccountPayload(
          values.currency,
          values.bank,
          values.payment_method as PublicAccountPaymentMethod,
          values.method_fields,
          values.alias,
        );
        const account = await createAccount(body);
        colocaToast.success(t("accounts_toast_success"));
        form.reset(defaults);
        onCreated?.(account);
      } catch (err) {
        colocaToast.error(HttpApiService.getErrorMessage(err) || t("accounts_toast_error"));
      } finally {
        onSubmittingChange?.(false);
      }
    },
    [createAccount, onCreated, onSubmittingChange, defaults, form, t],
  );

  const submitting = form.formState.isSubmitting;
  const fieldsDisabled = submitting || disabled;

  const paymentMethodItems = useMemo(() => {
    const all = [
      { value: PublicAccountPaymentMethodEnum.BANK, label: tAccountsWallets("form_payment_bank") },
      {
        value: PublicAccountPaymentMethodEnum.CRYPTO_WALLET,
        label: tAccountsWallets("form_payment_crypto"),
      },
      {
        value: PublicAccountPaymentMethodEnum.BINANCE_PAY,
        label: tAccountsWallets("form_payment_binance_pay"),
      },
    ];
    if (!currencyLocked) return all;
    // Locked currency → only the methods compatible with it (USDT: crypto wallet or Binance Pay; other
    // crypto: wallet only; fiat: bank only) — Binance Pay stays reachable for a USDT destination.
    return all.filter((o) =>
      o.value === PublicAccountPaymentMethodEnum.BINANCE_PAY
        ? initialCurrency === "USDT"
        : o.value === PublicAccountPaymentMethodEnum.CRYPTO_WALLET
          ? isCryptoWalletCurrency(initialCurrency)
          : !isCryptoWalletCurrency(initialCurrency),
    );
  }, [tAccountsWallets, currencyLocked, initialCurrency]);

  const bankItems = useMemo(
    () => getAccountFormBankOptions(currency, paymentMethod, isInternalUser),
    [currency, paymentMethod, isInternalUser],
  );

  return (
    <ColocaForm form={form} onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <ColocaComboboxField
          name="payment_method"
          label={tAccountsWallets("form_label_payment_method")}
          items={paymentMethodItems}
          placeholder={tAccountsWallets("form_select_generic")}
          emptyMessage={tGlobal("not_found_message")}
          disabled={fieldsDisabled}
          onValueChange={handlePaymentMethodChange}
          className="min-w-0"
        />
        <ColocaComboboxField
          name="currency"
          label={tConfirmations("form_label_currency")}
          items={CURRENCY_OPTIONS}
          placeholder={tConfirmations("form_placeholder_currency")}
          emptyMessage={tGlobal("not_found_message")}
          disabled={fieldsDisabled || currencyLocked}
          onValueChange={handleCurrencyChange}
          className="min-w-0"
        />
        <ColocaComboboxField
          name="bank"
          label={tConfirmations("form_label_bank")}
          items={bankItems}
          placeholder={
            currency
              ? tConfirmations("form_placeholder_bank")
              : tConfirmations("form_placeholder_bank_currency_first")
          }
          emptyMessage={tGlobal("not_found_message")}
          disabled={fieldsDisabled}
          className="min-w-0 sm:col-span-2"
        />
        <div className="min-w-0 sm:col-span-2">
          <ColocaInputField
            name="alias"
            label={t("accounts_field_alias_label")}
            placeholder={t("accounts_field_alias_placeholder")}
            maxLength={255}
            disabled={fieldsDisabled}
          />
        </div>
      </div>
      <PaymentMethodFieldsSection
        currency={currency}
        method={bank}
        value={methodFields}
        onChange={handleMethodFieldChange}
        errors={methodErrors}
        disabled={fieldsDisabled}
      />
      <div>
        <ColocaButton type="submit" variant="primary" disabled={fieldsDisabled} loading={submitting}>
          {t("accounts_btn_create")}
        </ColocaButton>
      </div>
    </ColocaForm>
  );
}
